import React from 'react';
import type { GatsbyBrowser } from 'gatsby';

import { MarcomThemeProvider } from './src/components/MarcomThemeProvider';
import { initSegment, initAmplitudeExperiment, skipAmplitudeExperiment } from './static/js/initialScripts';
import { storeCurrentAcquisitionContext } from './src/gatsby-browser/acquisition-context-tracking';
import { onConsentReady, ConsentState } from './src/utils/polarisConsent';
import { buildIntegrationsConfig } from './src/utils/segmentConfig';
import { GraphQLClientProvider } from './src/utils/graphql/GraphQLClientProvider';
import { BLISS_GATEWAY_ROOT_URL } from './src/utils/urlHelpers.util';
import { I18nProvider, type GatsbyPageContext, type LocaleNode } from './src/i18n/I18nProvider';
import { segment } from './static/js/joy';
import { initFallback } from './src/utils/imageFallback';
import { consentLogger } from './src/utils/consentLogger';

/**
 * Implement Gatsby's Browser APIs in this file.
 **/

initFallback(segment);

let isInitialized = false;

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
  const global = window;
  if ((global.location.search || '').replace('feature.marcom=', '').includes('feature.')) {
    // only load the query string parsing library
    // if there is a query string in the url
    import('./src/gatsby-browser/query-string-features').then((qs) => {
      qs.init();
    });
  }
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  global.scrollTo(0, 0);
  setTimeout(() => {
    isInitialized = true;
  }, 100);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({ routerProps: { location }, getSavedScrollPosition }) => {
  return isInitialized;
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = async () => {
  // Wait for Polaris consent to be determined
  // Note: In Gatsby, onClientEntry runs once and never unmounts, so cleanup is not necessary
  onConsentReady((consent: ConsentState) => {
    consentLogger.log('Consent determined, initializing analytics with granular consent');
    consentLogger.log('Consent state:', consent);

    // Build integrations config based on consent
    const integrations = buildIntegrationsConfig(consent);

    consentLogger.log('Segment integrations config:', integrations);

    // Initialize Segment with consent-based integrations
    initSegment(integrations);

    // Initialize telemetry (sets joyWsid cookie and identifies user)
    segment.initTelemetry();

    // Initialize Amplitude Experiment (only if statistics consent granted)
    if (consent.statistics) {
      consentLogger.log('Initializing Amplitude Experiment (statistics consent granted)');
      initAmplitudeExperiment();
    } else {
      consentLogger.log('Skipping Amplitude Experiment (no statistics consent)');
      // Unblock anything waiting on the experiment client — none is coming for this page.
      skipAmplitudeExperiment();
    }
  });

  // Every browser with native IntersectionObserver (all of them since 2019) was paying for this
  // shim in the app chunk. Browsers that genuinely lack it — IE 11 is still in our resolved
  // browserslist — still get it: Gatsby awaits `onClientEntry` before rendering (see
  // `apiRunnerAsync('onClientEntry')` in production-app), so the polyfill is installed before any
  // component can construct an observer. They trade an inline copy for one extra request.
  if (!('IntersectionObserver' in window)) {
    try {
      await import('intersection-observer');
    } catch (e) {
      // Gatsby awaits this promise before rendering, so letting a failed chunk fetch reject would
      // halt the startup runner and leave a dead page. Degrade instead: components that construct
      // an observer will throw individually, which is strictly better than never hydrating.
      console.error('Failed to load the IntersectionObserver polyfill', e);
    }
  }
};

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({ element }) => (
  <GraphQLClientProvider endpoint={`${BLISS_GATEWAY_ROOT_URL}/graphql`}>
    <MarcomThemeProvider>{element}</MarcomThemeProvider>
  </GraphQLClientProvider>
);

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = () => {
  // Runs on initial load + after subsequent route updates
  storeCurrentAcquisitionContext();
};

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({ element, props }) => {
  // Extract locales from page context (injected at build time)
  const pageContext = props.pageContext as GatsbyPageContext;
  const locales = (pageContext.locales as LocaleNode[] | undefined) ?? undefined;

  return (
    <I18nProvider pageContext={pageContext} locales={locales}>
      {element}
    </I18nProvider>
  );
};
